import styled, { css } from 'styled-components';
import { down } from 'styled-breakpoints';

/* Container uses negative margin to break out of PageContent padding (like Lobby) */
export const Container = styled.article`
  ${({ theme }) => css`
    margin: -${theme.spacing[6]};

    ${down('lg')} {
      margin: -${theme.spacing[4]};
    }

    ${down('md')} {
      margin: -${theme.spacing[3]};
    }
  `}
`;

/* ===== HERO BANNER ===== */

export const HeroBanner = styled.div`
  height: 340px;
  position: relative;
  overflow: hidden;
  width: 100%;
`;

export const HeroBannerBackground = styled.div<{
  $color: string;
  $banner?: string | null;
}>`
  position: absolute;
  inset: 0;

  ${({ $color, $banner }) =>
    $banner
      ? css`
          background-image: url(${$banner});
          background-size: cover;
          background-position: center;
        `
      : css`
          background: linear-gradient(
            145deg,
            #0d1117 0%,
            #161b22 30%,
            ${$color}18 60%,
            #0d1117 100%
          );
        `}
`;

export const HeroBannerPattern = styled.svg`
  width: 100%;
  height: 100%;
  opacity: 0.14;
`;

/* Overlay only at bottom — subtle fade, NO blur spreading to center */
export const HeroBannerOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 120px;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.7) 0%, transparent 100%);
  pointer-events: none;
`;

export const HeroBreadcrumb = styled.div`
  position: absolute;
  top: 20px;
  left: 32px;
  display: flex;
  align-items: center;
  gap: 10px;
  z-index: 5;
`;

export const BackButton = styled.button`
  width: 34px;
  height: 34px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.7);
  transition: ${({ theme }) => theme.transition.default};

  &:hover {
    background: rgba(255, 255, 255, 0.15);
  }
`;

export const BreadcrumbText = styled.span`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.75);
`;

export const BreadcrumbSeparator = styled.span`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.2);
`;

export const BreadcrumbCurrent = styled.span`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.52);
  font-weight: 600;
`;

export const CategoryBadge = styled.div<{ $color: string }>`
  position: absolute;
  top: 20px;
  right: 32px;
  z-index: 5;
  padding: 5px 14px;
  border-radius: 8px;
  background: linear-gradient(
    135deg,
    ${({ $color }) => $color}88,
    ${({ $color }) => $color}cc
  );
  backdrop-filter: blur(10px);
  color: white;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

export const HeroContent = styled.div`
  position: absolute;
  bottom: 28px;
  left: 32px;
  right: 32px;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  z-index: 5;

  ${down('md')} {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
`;

export const HeroLeft = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 20px;
`;

export const HeroLogo = styled.div`
  width: 140px;
  height: 140px;
  border-radius: 24px;
  background: rgb(255, 255, 255);
  backdrop-filter: blur(16px);
  border: 1px solid rgba(255, 255, 255, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;

  > img {
    width: 80%;
    height: 80%;
    object-fit: contain;
  }
`;

export const HeroInfo = styled.div`
  margin-bottom: 4px;

  > h1 {
    font-size: 30px;
    font-weight: 800;
    color: white;
    letter-spacing: -0.5px;
    margin-bottom: 6px;
  }
`;

export const HeroMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`;

export const HeroMetaItem = styled.span`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
  display: flex;
  align-items: center;
  gap: 5px;
`;

export const HeroActions = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 4px;
`;

export const HeroCTAButton = styled.button<{ $color: string }>`
  padding: 10px 24px;
  border-radius: 12px;
  border: none;
  background: ${({ $color }) => $color};
  color: white;
  font-size: 13px;
  font-weight: 600;
  font-family: inherit;
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  transition: all 0.25s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px ${({ $color }) => $color}33;
  }
`;

export const HeroGhostButton = styled.button`
  padding: 10px 24px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: rgba(255, 255, 255, 0.06);
  backdrop-filter: blur(10px);
  color: white;
  font-size: 13px;
  font-weight: 600;
  font-family: inherit;
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  transition: all 0.25s ease;

  &:hover {
    transform: translateY(-2px);
    background: rgba(255, 255, 255, 0.12);
  }
`;

/* ===== CONTENT LAYOUT ===== */

export const ContentWrapper = styled.div`
  display: flex;
  gap: 28px;
  padding: 28px 32px;

  ${down('lg')} {
    flex-direction: column;
    padding: 20px 16px;
  }
`;

export const MainContent = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-left: 2.5rem;
`;

export const Sidebar = styled.div`
  ${({ theme }) => css`
    width: 320px;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    gap: 16px;
    position: sticky;
    top: calc(${theme.layout.header} + 16px);
    align-self: flex-start;
    max-height: calc(100vh - ${theme.layout.header} - 32px);
    overflow-y: auto;

    &::-webkit-scrollbar {
      width: 4px;
    }
    &::-webkit-scrollbar-track {
      background: transparent;
    }
    &::-webkit-scrollbar-thumb {
      background: ${theme.colors.background[40]};
      border-radius: 4px;
    }

    ${down('lg')} {
      width: 100%;
      position: static;
      max-height: none;
    }
  `}
`;

/* ===== CARDS ===== */

export const Card = styled.div`
  background: white;
  border-radius: 16px;
  border: 1px solid ${({ theme }) => theme.colors.base.border};
  padding: 24px 26px;
`;

export const CardTitle = styled.h2`
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 14px;
`;

export const CardTitleWithCount = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;

  > h2 {
    font-size: 16px;
    font-weight: 700;
  }

  > span {
    font-size: 12px;
    color: #94a3b8;
  }
`;

/* ===== ABOUT ===== */

/* 5 lines collapse: 13px font * 1.75 line-height * 5 ≈ 114px */
export const AboutText = styled.div<{
  $expanded: boolean;
  $collapsible?: boolean;
}>`
  font-size: 13px;
  color: #4a5568;
  line-height: 1.75;
  max-height: ${({ $expanded, $collapsible }) =>
    !$collapsible || $expanded ? 'none' : '114px'};
  overflow: hidden;
  position: relative;

  p {
    margin-bottom: 12px;
  }
`;

export const AboutFade = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 50px;
  background: linear-gradient(transparent, white);
`;

export const ReadMoreButton = styled.button<{ $color: string }>`
  background: none;
  border: none;
  color: ${({ $color }) => $color};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  margin-top: 8px;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0;

  > svg {
    transition: transform 0.2s;
  }
`;

/* ===== FILES ===== */

export const FileList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const FileRow = styled.a`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 10px;
  text-decoration: none;
  color: inherit;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.brand.primary.opaque};
    transform: translateX(3px);
  }
`;

export const FileIcon = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

export const FileInfo = styled.div`
  flex: 1;

  > div:first-child {
    font-size: 13px;
    font-weight: 600;
    color: #1a1a2e;
  }

  > div:last-child {
    font-size: 11px;
    color: #b0b8c4;
  }
`;

/* ===== PRODUCTS ===== */

export const ProductList = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: 2rem;

  > li {
    border-radius: 0.5rem;
    overflow: hidden;
    box-shadow: ${({ theme }) => theme.shadows.sm};
    transition: 180ms 80ms ease-in-out;

    button {
      all: initial;
      cursor: pointer;
      width: 7rem;
      height: 7rem;
      background: none;
      border: none;
      padding: 0;

      img {
        width: 100%;
        height: 100%;
        object-fit: contain;
      }
    }

    &:hover {
      transform: scale3d(1.05, 1.05, 1);
      box-shadow: ${({ theme }) => theme.shadows.md};
    }
  }
`;

/* ===== SIDEBAR CARDS ===== */

export const SidebarCard = styled.div`
  background: white;
  border-radius: 16px;
  border: 1px solid ${({ theme }) => theme.colors.base.border};
  padding: 20px 22px;
`;

export const SidebarCardTitle = styled.h3`
  font-size: 13px;
  font-weight: 700;
  margin-bottom: 14px;
`;

export const WebsiteLink = styled.a<{ $color: string }>`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: ${({ $color }) => $color};
  font-weight: 500;
  text-decoration: none;
  transition: ${({ theme }) => theme.transition.default};

  &:hover {
    opacity: 0.8;
  }
`;

export const ContactButton = styled.button<{ $color: string }>`
  width: 100%;
  padding: 10px 0;
  border-radius: 10px;
  background: ${({ $color }) => $color};
  color: white;
  border: none;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: all 0.25s ease;

  &:hover {
    box-shadow: 0 4px 14px ${({ $color }) => $color}33;
  }
`;

/* ===== SOCIAL LINKS (sidebar) ===== */

export const SocialLinksList = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const SocialLinkIcon = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.brand.primary.default};
  transition: all 0.2s ease;

  &:hover {
    opacity: 0.7;
    transform: scale(1.1);
  }
`;

/* ===== SKELETON ===== */

export const SkeletonHero = styled.div`
  height: 340px;
  background: #f0f0f0;
`;

export const SkeletonContent = styled.div`
  display: flex;
  gap: 28px;
  padding: 28px 32px;

  ${down('lg')} {
    flex-direction: column;
  }
`;

export const SkeletonMain = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

export const SkeletonSidebar = styled.div`
  width: 280px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;

  ${down('lg')} {
    width: 100%;
  }
`;
